'use client';

import React, { ReactNode, Suspense, lazy, useEffect } from 'react';
import { GSAPProvider } from '@/contexts/GSAPContext';
import { HeaderProvider } from '@/contexts/HeaderContext';
import { MediaProvider } from '@/contexts/MediaContext';
import { DeferNonCriticalCSS } from '@/components/performance/DeferNonCriticalCSS';
import { registerServiceWorker } from '@/lib/service-worker-registration';

// VideoAutoplayConsent handles mobile video autoplay and dispatches global events
// Should be loaded early to enable video playback on mobile
const VideoAutoplayConsent = lazy(() =>
  import('@/components/shared/VideoAutoplayConsent')
);

// FloatingWhatsAppButton - botón flotante de contacto, presente en todas las páginas
const FloatingWhatsAppButton = lazy(() =>
  import('@/components/shared/FloatingWhatsAppButton')
);

// SourceTracker - captures the marketing `from` URL param early for attribution
const SourceTracker = lazy(() =>
  import('@/components/shared/SourceTracker')
);

// DevHeader only needed in development - don't include in production bundle
const DevHeader = process.env.NODE_ENV === 'development'
  ? lazy(() => import('@/components/dev/DevHeader'))
  : () => null;

interface ClientProvidersProps {
  children: ReactNode;
}

/**
 * ClientProviders - Wrapper for all client-side providers
 * 
 * PERFORMANCE OPTIMIZATIONS:
 * 1. Excludes DevHeader from production builds entirely
 * 2. Defers optional Service Worker registration
 */
export function ClientProviders({ children }: ClientProvidersProps) {
  // Register Service Worker for background caching (optional enhancement)
  useEffect(() => {
    // Service worker stays opt-in to avoid background traffic and update checks on public visits
    if (process.env.NEXT_PUBLIC_ENABLE_SW !== 'true') {
      return;
    }

    const timeoutId = setTimeout(() => {
      registerServiceWorker().catch(() => {
        // Silently fail - Service Worker is optional
      });
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <>
      {/* Defer non-critical CSS to reduce render-blocking */}
      <DeferNonCriticalCSS />
      {/* VideoAutoplayConsent - handles mobile video autoplay, loads early for user interactions */}
      <Suspense fallback={null}>
        <VideoAutoplayConsent />
      </Suspense>
      {/* SourceTracker - capture marketing `from` param ASAP for form attribution */}
      <Suspense fallback={null}>
        <SourceTracker />
      </Suspense>
      {/* DevHeader only renders in development */}
      {process.env.NODE_ENV === 'development' && (
        <Suspense fallback={null}>
          <DevHeader />
        </Suspense>
      )}
      <GSAPProvider>
        <HeaderProvider>
          <MediaProvider>
            {children}
          </MediaProvider>
        </HeaderProvider>
      </GSAPProvider>
      {/* Floating WhatsApp contact button - visible across all pages */}
      <Suspense fallback={null}>
        <FloatingWhatsAppButton />
      </Suspense>
    </>
  );
}

export default ClientProviders;
